/**
 * ActionItemCard — renders a single unified action item.
 *
 * Pre-migration-0081 this component branched heavily on `source`
 * (insight/task/alert) to choose between separate action handlers. The
 * unified model collapses everything into a single typed item with
 * status transitions (snooze / complete / dismiss / not_applicable),
 * so the component now exposes one onAction handler. Kind-specific
 * styling lives in display constants in shared.ts.
 */

import { useState, useRef, useEffect } from "react";
import {
  CheckCircle2,
  Lightbulb,
  ExternalLink,
  ChevronDown,
  ChevronRight,
  MoreHorizontal,
  Clock,
  CheckCircle,
  Ban,
} from "lucide-react";
import {
  type UnifiedItem,
  BORDER_COLORS,
  SEVERITY_BADGE,
  SOURCE_BADGE,
  categoryLabel,
} from "./shared";

export type ActionItemAction =
  | "snooze"
  | "complete"
  | "dismiss"
  | "not_applicable";

// ============================================================================
// Confidence Badge
// ============================================================================

const CONFIDENCE_CONFIG: Record<
  "high" | "medium" | "low",
  { className: string; tooltip: string }
> = {
  high: {
    className: "bg-emerald-100 text-emerald-700",
    tooltip: "Verified by DNS records and/or high message volume",
  },
  medium: {
    className: "bg-amber-100 text-amber-700",
    tooltip: "Based on available data — verify before acting",
  },
  low: {
    className: "bg-slate-100 text-slate-600",
    tooltip: "Limited data — needs investigation",
  },
};

export function ConfidenceBadge({
  confidence,
}: {
  confidence: "high" | "medium" | "low";
}) {
  const config = CONFIDENCE_CONFIG[confidence];
  return (
    <span
      className={`px-2 py-0.5 rounded text-xs font-medium ${config.className} cursor-help`}
      title={config.tooltip}
    >
      {confidence} confidence
    </span>
  );
}

// ============================================================================
// Insight Details — auth rates pulled from item.metadata when applicable
// ============================================================================

export function InsightDetails({ metadata }: { metadata: Record<string, unknown> }) {
  const spfAuth = typeof metadata.spfAuthRate === "number"
    ? Math.round(metadata.spfAuthRate * 100)
    : typeof metadata.spfAuthPassRate === "number"
      ? (metadata.spfAuthPassRate as number)
      : null;
  const spfAlign = typeof metadata.spfAlignRate === "number"
    ? Math.round(metadata.spfAlignRate * 100)
    : typeof metadata.spfPassRate === "number"
      ? (metadata.spfPassRate as number)
      : null;
  const dkimAuth = typeof metadata.dkimAuthRate === "number"
    ? Math.round(metadata.dkimAuthRate * 100)
    : typeof metadata.dkimAuthPassRate === "number"
      ? (metadata.dkimAuthPassRate as number)
      : null;
  const dkimAlign = typeof metadata.dkimAlignRate === "number"
    ? Math.round(metadata.dkimAlignRate * 100)
    : typeof metadata.dkimPassRate === "number"
      ? (metadata.dkimPassRate as number)
      : null;

  const hasAuthRates = spfAuth != null || dkimAuth != null;
  if (!hasAuthRates) return null;

  return (
    <div className="flex gap-3 flex-wrap">
      {spfAuth != null && (
        <div className="bg-slate-50 rounded-lg p-3 border border-slate-200 min-w-[180px]">
          <p className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-2">SPF</p>
          <div className="space-y-1">
            <div className="flex justify-between text-sm">
              <span className="text-slate-600">Authentication:</span>
              <span className="font-medium text-slate-800">{spfAuth}%</span>
            </div>
            {spfAlign != null && (
              <div className="flex justify-between text-sm">
                <span className="text-slate-600">Alignment:</span>
                <span className="font-medium text-slate-800">{spfAlign}%</span>
              </div>
            )}
            {spfAuth > 50 && spfAlign != null && spfAlign < 50 && (
              <p className="text-xs text-amber-600 mt-1">
                Auth passes but alignment fails — envelope-from domain mismatch
              </p>
            )}
          </div>
        </div>
      )}
      {dkimAuth != null && (
        <div className="bg-slate-50 rounded-lg p-3 border border-slate-200 min-w-[180px]">
          <p className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-2">DKIM</p>
          <div className="space-y-1">
            <div className="flex justify-between text-sm">
              <span className="text-slate-600">Authentication:</span>
              <span className="font-medium text-slate-800">{dkimAuth}%</span>
            </div>
            {dkimAlign != null && (
              <div className="flex justify-between text-sm">
                <span className="text-slate-600">Alignment:</span>
                <span className="font-medium text-slate-800">{dkimAlign}%</span>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================================================
// Action menu — unified replacement for the separate per-kind menus
// ============================================================================

function ActionMenu({
  itemId,
  status,
  onAction,
}: {
  itemId: string;
  status: UnifiedItem["status"];
  onAction: (id: string, action: ActionItemAction) => void;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [open]);

  // No actions available once the item has terminal status.
  if (status !== "active") return null;

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen((v) => !v)}
        className="p-1.5 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
        title="Item actions"
      >
        <MoreHorizontal className="w-4 h-4" />
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-1 w-48 bg-white border border-slate-200 rounded-lg shadow-lg z-20">
          <button
            onClick={() => {
              onAction(itemId, "snooze");
              setOpen(false);
            }}
            className="flex items-center gap-2 w-full px-3 py-2 text-sm text-slate-700 hover:bg-slate-50 rounded-t-lg"
          >
            <Clock className="w-4 h-4 text-amber-500 flex-shrink-0" />
            Snooze 30 days
          </button>
          <button
            onClick={() => {
              onAction(itemId, "complete");
              setOpen(false);
            }}
            className="flex items-center gap-2 w-full px-3 py-2 text-sm text-slate-700 hover:bg-slate-50"
          >
            <CheckCircle className="w-4 h-4 text-emerald-500 flex-shrink-0" />
            Mark Completed
          </button>
          <button
            onClick={() => {
              onAction(itemId, "not_applicable");
              setOpen(false);
            }}
            className="flex items-center gap-2 w-full px-3 py-2 text-sm text-slate-700 hover:bg-slate-50 rounded-b-lg"
          >
            <Ban className="w-4 h-4 text-slate-400 flex-shrink-0" />
            Not Applicable
          </button>
        </div>
      )}
    </div>
  );
}

// ============================================================================
// Action Item Card
// ============================================================================

export function ActionItemCard({
  item,
  id,
  highlighted,
  onNavigate,
  onAction,
}: {
  item: UnifiedItem;
  id?: string;
  highlighted?: boolean;
  onNavigate: (path: string) => void;
  onAction: (id: string, action: ActionItemAction) => void;
}) {
  const [expanded, setExpanded] = useState(item.severity === "critical");

  // Recommendations are stored as `unknown[]` on the unified row; coerce
  // to strings for rendering. Unknown shapes are dropped silently.
  const recommendations: string[] = Array.isArray(item.recommendations)
    ? item.recommendations.filter((r): r is string => typeof r === "string")
    : [];

  // Quick-action button text varies by kind: tasks get "Done", alerts get
  // "Resolve", insights get nothing (only the menu).
  const quickAction =
    item.status === "active"
      ? item.kind === "task"
        ? { label: "Done", icon: <CheckCircle2 className="w-3.5 h-3.5" />, action: "complete" as const }
        : item.kind === "alert"
          ? { label: "Resolve", icon: null, action: "complete" as const }
          : null
      : null;

  return (
    <div
      id={id}
      className={`bg-white rounded-xl border border-slate-200 border-l-4 ${
        BORDER_COLORS[item.severity] || BORDER_COLORS.info
      }${
        highlighted
          ? " ring-2 ring-amber-400 shadow-md transition-all duration-300"
          : ""
      }`}
    >
      <div className="p-3 sm:p-4">
        {/* Header row */}
        <div className="flex items-start gap-2 sm:gap-3">
          <button
            onClick={() => setExpanded(!expanded)}
            className="mt-0.5 text-slate-400 hover:text-slate-600 transition-colors flex-shrink-0"
          >
            {expanded ? (
              <ChevronDown className="w-4 h-4" />
            ) : (
              <ChevronRight className="w-4 h-4" />
            )}
          </button>

          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-1.5 sm:gap-2 mb-1 flex-wrap">
              <h3
                className="text-sm font-semibold text-slate-800 cursor-pointer hover:text-purple-700"
                onClick={() => setExpanded(!expanded)}
              >
                {item.title}
              </h3>
              <span
                className={`px-1.5 sm:px-2 py-0.5 rounded text-xs font-medium ${
                  SEVERITY_BADGE[item.severity] || SEVERITY_BADGE.info
                }`}
              >
                {item.severity}
              </span>
              <span
                className={`px-1.5 sm:px-2 py-0.5 rounded text-xs font-medium ${SOURCE_BADGE[item.kind].className}`}
              >
                {SOURCE_BADGE[item.kind].label}
              </span>
              <span className="hidden sm:inline-block px-2 py-0.5 rounded text-xs font-medium bg-slate-100 text-slate-500">
                {categoryLabel(item.category)}
              </span>
              {item.confidence && item.kind === "insight" && (
                <span className="hidden sm:inline-block">
                  <ConfidenceBadge confidence={item.confidence} />
                </span>
              )}
            </div>
            <p
              className={`text-sm text-slate-600 ${
                expanded ? "" : "line-clamp-2"
              }`}
            >
              {item.description}
            </p>
          </div>

          {/* Actions — hidden on mobile, shown inline on sm+ */}
          <div className="hidden sm:flex gap-2 flex-shrink-0 items-center">
            {item.internalActionLink && (
              <button
                onClick={() => onNavigate(item.internalActionLink!)}
                className="flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-purple-700 hover:bg-purple-50 rounded-lg transition-colors"
              >
                View
                <ExternalLink className="w-3.5 h-3.5" />
              </button>
            )}
            {quickAction && (
              <button
                onClick={() => onAction(item.id, quickAction.action)}
                className="flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-emerald-700 hover:bg-emerald-50 rounded-lg transition-colors"
              >
                {quickAction.icon}
                {quickAction.label}
              </button>
            )}
            <ActionMenu itemId={item.id} status={item.status} onAction={onAction} />
          </div>
        </div>

        {/* Mobile actions */}
        <div className="flex gap-2 flex-wrap mt-2 ml-6 sm:hidden">
          {item.internalActionLink && (
            <button
              onClick={() => onNavigate(item.internalActionLink!)}
              className="flex items-center gap-1 px-2.5 py-1 text-sm font-medium text-purple-700 hover:bg-purple-50 rounded-lg transition-colors"
            >
              View
              <ExternalLink className="w-3.5 h-3.5" />
            </button>
          )}
          {quickAction && (
            <button
              onClick={() => onAction(item.id, quickAction.action)}
              className="flex items-center gap-1 px-2.5 py-1 text-sm font-medium text-emerald-700 hover:bg-emerald-50 rounded-lg transition-colors"
            >
              {quickAction.icon}
              {quickAction.label}
            </button>
          )}
          <ActionMenu itemId={item.id} status={item.status} onAction={onAction} />
        </div>

        {/* Expanded body: auth rates + recommendations */}
        {expanded && (
          <div className="mt-4 ml-7 space-y-3">
            {item.kind === "insight" && item.metadata && (
              <InsightDetails metadata={item.metadata} />
            )}

            {recommendations.length > 0 && (
              <div className="bg-slate-50 rounded-lg p-4">
                <p className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-3">
                  Recommendations
                </p>
                <ul className="space-y-2">
                  {recommendations.map((rec, idx) => (
                    <li
                      key={idx}
                      className="flex items-start gap-2.5 text-sm text-slate-700"
                    >
                      <Lightbulb className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" />
                      <span>{rec}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}

        {/* Footer */}
        <div className="flex items-center gap-4 mt-2 ml-7 text-xs text-slate-400">
          <span>{new Date(item.createdAt).toLocaleDateString()}</span>
          {item.domainName && (
            <span className="text-slate-500">{item.domainName}</span>
          )}
        </div>
      </div>
    </div>
  );
}
